import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  IconButton,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Payments as PaymentsIcon,
  RequestQuote as RequestQuoteIcon,
} from '@mui/icons-material';
import { visitAPI, invoiceAPI, paymentAPI, feeConfigAPI, settingsAPI } from '../services/api';
import PrintableInvoice from '../components/PrintableInvoice';
import PrintableReceipt from '../components/PrintableReceipt';
import PrintPortal from '../components/PrintPortal';
import StatCard from '../components/StatCard';

const Billing = () => {
  const [tabValue, setTabValue] = useState(0);
  const [visits, setVisits] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [daySummary, setDaySummary] = useState(null);
  const [feeConfigs, setFeeConfigs] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [discount, setDiscount] = useState(0);

  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', mode: 'cash', transactionId: '', notes: '' });

  const [printInvoice, setPrintInvoice] = useState(null);
  const [printReceipt, setPrintReceipt] = useState(null);

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchAll = useCallback(async () => {
    try {
      const [visitRes, invoiceRes, feeRes, settingsRes] = await Promise.all([
        visitAPI.getTodayVisits(),
        invoiceAPI.listInvoices({ limit: 30 }),
        feeConfigAPI.getFeeConfigs({ activeOnly: 'true' }),
        settingsAPI.getSettings(),
      ]);
      setVisits(visitRes.data.data);
      setInvoices(invoiceRes.data.data);
      setFeeConfigs(feeRes.data.data);
      setClinic(settingsRes.data.data);
    } catch (error) {
      showSnackbar('Failed to load billing data', 'error');
    }
  }, []);

  const fetchDaySummary = async () => {
    try {
      const res = await paymentAPI.getDaySummary();
      setDaySummary(res.data.data);
    } catch (error) {
      showSnackbar('Failed to load day summary', 'error');
    }
  };

  useEffect(() => { fetchAll(); fetchDaySummary(); }, [fetchAll]);

  const openCreateInvoice = (visit) => {
    setSelectedVisit(visit);
    setInvoiceItems([{ description: '', hsnSacCode: '', quantity: 1, unitPrice: 0, gstRate: 0 }]);
    setDiscount(0);
    setOpenInvoiceDialog(true);
  };

  const addItemFromFee = (fee) => {
    setInvoiceItems([...invoiceItems, {
      description: fee.name, hsnSacCode: fee.hsnSacCode || '', quantity: 1, unitPrice: fee.amount, gstRate: fee.gstRate || 0
    }]);
  };

  const addBlankItem = () => {
    setInvoiceItems([...invoiceItems, { description: '', hsnSacCode: '', quantity: 1, unitPrice: 0, gstRate: 0 }]);
  };

  const removeItem = (index) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index][field] = value;
    setInvoiceItems(newItems);
  };

  const calcInvoiceTotal = () => {
    const subtotal = invoiceItems.reduce((sum, it) => sum + (it.quantity * it.unitPrice || 0), 0);
    const tax = invoiceItems.reduce((sum, it) => sum + (it.quantity * it.unitPrice * (it.gstRate / 100) || 0), 0);
    return { subtotal, tax, total: subtotal - discount + tax };
  };

  const handleCreateInvoice = async () => {
    if (invoiceItems.length === 0 || invoiceItems.some(i => !i.description || !i.unitPrice)) {
      showSnackbar('Please complete all line items', 'error');
      return;
    }
    try {
      const res = await invoiceAPI.createInvoice({
        visitId: selectedVisit._id,
        items: invoiceItems,
        discount: parseFloat(discount) || 0,
      });
      showSnackbar('Invoice generated successfully!');
      setOpenInvoiceDialog(false);
      fetchAll();
      handlePrintInvoice(res.data.data._id);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to create invoice', 'error');
    }
  };

  const handlePrintInvoice = async (invoiceId) => {
    try {
      const res = await invoiceAPI.getInvoiceById(invoiceId);
      setPrintInvoice(res.data.data);
      setTimeout(() => window.print(), 300);
    } catch (error) {
      showSnackbar('Failed to load invoice for printing', 'error');
    }
  };

  const openCollectPayment = (visit, invoice) => {
    const balance = invoice
      ? (invoice.balanceAmount ?? 0)
      : Math.max(0, (visit?.totalAmount || 0) - (visit?.paidAmount || 0));
    setPaymentTarget({ visit, invoice });
    setPaymentForm({ amount: balance > 0 ? balance : '', mode: 'cash', transactionId: '', notes: '' });
    setOpenPaymentDialog(true);
  };

  const handleCollectPayment = async () => {
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) {
      showSnackbar('Please enter a valid amount', 'error');
      return;
    }
    // visit may be a populated doc, an id, or only reachable via the invoice
    const visitId = paymentTarget.visit?._id || paymentTarget.visit ||
      paymentTarget.invoice?.visit?._id || paymentTarget.invoice?.visit;
    const patientId = paymentTarget.visit?.patient?._id || paymentTarget.visit?.patient ||
      paymentTarget.invoice?.patient?._id || paymentTarget.invoice?.patient;
    const balance = paymentTarget.invoice
      ? (paymentTarget.invoice.balanceAmount ?? 0)
      : (paymentTarget.visit?.totalAmount || 0) - (paymentTarget.visit?.paidAmount || 0);
    const billedTotal = paymentTarget.invoice
      ? (paymentTarget.invoice.totalAmount ?? 0)
      : (paymentTarget.visit?.totalAmount || 0);
    if (billedTotal > 0 && balance <= 0) {
      showSnackbar('This visit is already fully paid', 'error');
      return;
    }
    if (balance > 0 && amt > balance + 0.01) {
      showSnackbar(`Amount exceeds outstanding balance of ₹${balance.toFixed(2)}`, 'error');
      return;
    }
    try {
      const res = await paymentAPI.createPayment({
        visitId,
        patientId,
        invoiceId: paymentTarget.invoice?._id,
        amount: amt,
        mode: paymentForm.mode,
        transactionId: paymentForm.transactionId,
        notes: paymentForm.notes,
        type: billedTotal <= 0 ? 'advance' : amt >= balance ? 'full' : 'partial',
      });
      showSnackbar('Payment recorded successfully!');
      setOpenPaymentDialog(false);
      fetchAll();
      fetchDaySummary();
      setPrintReceipt(res.data.data);
      setTimeout(() => window.print(), 300);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed to record payment', 'error');
    }
  };

  const { subtotal, tax, total } = calcInvoiceTotal();

  return (
    <Box>
      {/* Rendered via portal directly into document.body so it survives the .no-print layout */}
      <PrintPortal>
        {printInvoice && <PrintableInvoice clinic={printInvoice.clinic} invoice={printInvoice.invoice} />}
        {printReceipt && <PrintableReceipt clinic={clinic} payment={printReceipt} />}
      </PrintPortal>

      <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 'none' }} className="no-print">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(124, 132, 212, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
              <RequestQuoteIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="700">Billing &amp; Payments</Typography>
              <Typography variant="body2" color="text.secondary">GST invoicing, partial payments &amp; daily collection</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => { fetchAll(); fetchDaySummary(); }} sx={{ border: '1px solid #E9E9F2', color: 'text.secondary', '&:hover': { bgcolor: '#F4F4F9' } }}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {daySummary && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <StatCard label="Today's Collection" value={`₹${daySummary.totalCollection.toFixed(2)}`} icon={<PaymentsIcon fontSize="small" />} color="primary" />
            <StatCard label="Transactions" value={daySummary.totalTransactions} icon={<RequestQuoteIcon fontSize="small" />} color="info" />
            {daySummary.byMode.map((m) => (
              <StatCard key={m._id} label={m._id} value={`₹${m.total.toFixed(2)}`} color="secondary" />
            ))}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ mb: 2, boxShadow: 'none' }} className="no-print">
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Visits Pending Billing" />
          <Tab label="Recent Invoices" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }} className="no-print">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Token</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Visit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total (₹)</TableCell>
                  <TableCell align="right">Paid (₹)</TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visits.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No visits today</Typography>
                  </TableCell></TableRow>
                ) : visits.map((visit) => (
                  <TableRow key={visit._id} hover>
                    <TableCell><Chip size="small" label={`#${visit.tokenNumber || '-'}`} color="primary" /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.light' }}>{visit.patient?.name?.charAt(0)}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{visit.patient?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{visit.patient?.patientId}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="body2">{visit.visitId}</Typography></TableCell>
                    <TableCell><Chip size="small" label={visit.status.replace(/-/g, ' ')} sx={{ textTransform: 'capitalize' }} /></TableCell>
                    <TableCell align="right">₹{(visit.totalAmount || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">₹{(visit.paidAmount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={visit.paymentStatus} color={visit.paymentStatus === 'paid' ? 'success' : visit.paymentStatus === 'partial' ? 'warning' : 'error'} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => openCreateInvoice(visit)} sx={{ mr: 1 }}>Invoice</Button>
                      {((visit.totalAmount || 0) <= 0 || (visit.paidAmount || 0) < visit.totalAmount) && (
                        <Button size="small" variant="contained" color="success" startIcon={<PaymentsIcon />} onClick={() => openCollectPayment(visit, null)}>Collect</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }} className="no-print">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Total (₹)</TableCell>
                  <TableCell align="right">Balance (₹)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No invoices generated yet</Typography>
                  </TableCell></TableRow>
                ) : invoices.map((inv) => (
                  <TableRow key={inv._id} hover>
                    <TableCell><Typography variant="body2" fontWeight={700}>{inv.invoiceNumber}</Typography></TableCell>
                    <TableCell>{inv.patient?.name}</TableCell>
                    <TableCell>{new Date(inv.createdAt).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell align="right">₹{inv.totalAmount.toFixed(2)}</TableCell>
                    <TableCell align="right">₹{inv.balanceAmount.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={inv.paymentStatus} color={inv.paymentStatus === 'paid' ? 'success' : inv.paymentStatus === 'partial' ? 'warning' : 'error'} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handlePrintInvoice(inv._id)}><PrintIcon fontSize="small" /></IconButton>
                      {inv.balanceAmount > 0 && (
                        <Button size="small" variant="contained" color="success" onClick={() => openCollectPayment(inv.visit, inv)}>Collect Balance</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={openInvoiceDialog} onClose={() => setOpenInvoiceDialog(false)} maxWidth="md" fullWidth className="no-print">
        <DialogTitle>
          Generate Invoice — {selectedVisit?.patient?.name}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {feeConfigs.map((fee) => (
              <Chip key={fee._id} label={`+ ${fee.name} (₹${fee.amount})`} onClick={() => addItemFromFee(fee)} clickable />
            ))}
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell width={100}>HSN/SAC</TableCell>
                <TableCell width={70}>Qty</TableCell>
                <TableCell width={100}>Rate</TableCell>
                <TableCell width={80}>GST%</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceItems.map((item, i) => (
                <TableRow key={i}>
                  <TableCell><TextField size="small" fullWidth value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" value={item.hsnSacCode} onChange={(e) => updateItem(i, 'hsnSacCode', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', parseFloat(e.target.value) || 1)} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={item.unitPrice} onChange={(e) => updateItem(i, 'unitPrice', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" value={item.gstRate} onChange={(e) => updateItem(i, 'gstRate', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeItem(i)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Button startIcon={<AddIcon />} onClick={addBlankItem} sx={{ mt: 1 }}>Add Line Item</Button>

          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Discount (₹)" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2">Subtotal: ₹{subtotal.toFixed(2)}</Typography>
                <Typography variant="body2">Tax: ₹{tax.toFixed(2)}</Typography>
                <Typography variant="h6" fontWeight={700}>Total: ₹{total.toFixed(2)}</Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenInvoiceDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateInvoice}>Generate &amp; Print Invoice</Button>
        </DialogActions>
      </Dialog>

      {/* Collect Payment Dialog */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="xs" fullWidth className="no-print">
        <DialogTitle>Collect Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Amount *" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label="Payment Mode" value={paymentForm.mode} onChange={(e) => setPaymentForm({ ...paymentForm, mode: e.target.value })}>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="netbanking">Net Banking</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="insurance">Insurance</MenuItem>
              </TextField>
            </Grid>
            {paymentForm.mode !== 'cash' && (
              <Grid item xs={12}>
                <TextField fullWidth label="Transaction ID" value={paymentForm.transactionId} onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleCollectPayment}>Collect &amp; Print Receipt</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} className="no-print">
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Billing;
